import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, take, tap } from 'rxjs/operators';

import { CalendarTypeModel } from '@components/report-filters/filters/calendars-filter/models/calendarTypeModel';
import { DEFAULT_LANGUAGE, EVENT_SELECTOR_SELECT } from '@config/global-constants';
import { add_job_categories, reset_filters, set_business_rule_group } from '@rpt-ngrx/actions/filters.action';
import {
    reset_report_etl_statistics,
    select_report,
    set_report_filters,
    set_report_url,
} from '@rpt-ngrx/actions/reports.action';
import { add_sites_failure } from '@rpt-ngrx/actions/sites.action';
import { selectActiveReport } from '@rpt-ngrx/selectors/reports.selector';
import { ErrorHandlerService } from '@services/helper';
import { CustomHttpClient, PortalHttpClient } from '@services/http-client';
import { buildIdentifyContext } from '@services/http-client/http-context.utils';
import { NEEDS_X_LOCALES } from '@services/interceptors/http-context-tokens';
import { NotificationService } from '@services/notification/notification.service';
import { PreferencesService } from '@services/preferences/preferences.service';
import { ENTITY_TYPES } from '@shared/enums/entityTypes.enum';
import { IEventPeriodDataResp, IEventsResponse, ISite, ISitesResponse, QuestionType } from '@shared/types';
import { IPublishedReport, IReport } from '@shared/types/report';
import { getMaxDate, getMinDate } from '@shared/utils';

@Injectable()
export class ReportsService {
    private currentReportURI: string = '';
    private currentSiteIds: string = '';
    public loadReport$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public onReloadReport$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private http: CustomHttpClient,
        private router: Router,
        private store: Store,
        private errorHandlerService: ErrorHandlerService,
        private portalHttp: PortalHttpClient,
        private translations: TranslateService,
        private notificationService: NotificationService,
        private preferences: PreferencesService,
    ) {}

    public set loadReport(value) {
        this.loadReport$.next(value);
    }

    public getCurrentSiteIds(): string {
        return this.currentSiteIds;
    }

    public fetchReports() {
        const hasLiabilitySalesIntoGrossSales = this.preferences.companyHasLiabilitySalesIntoGrossSales;
        const hasPreTenderVoidAmounts = this.preferences.companyHasPreTenderVoidAmounts;
        const hasStartingAmount = this.preferences.companyHasStartingAmount;

        return this.http
            .getReports({ hasLiabilitySalesIntoGrossSales, hasPreTenderVoidAmounts, hasStartingAmount })
            .pipe(
                shareReplay(),
                tap((data: { articleCount: number; reports: Array<IReport | IPublishedReport> }) =>
                    data.reports.forEach((report) => {
                        report.reportID = report.id;
                        if (report.isPublished) {
                            report.id = report.name.toLowerCase().split(' ').join('_');
                        }
                    }),
                ),
            );
    }

    public fetchReportOptions(id: string, isDetail: boolean): Observable<IReport> {
        const headers = new HttpHeaders({
            'x-company-id': this.http.getCompany(),
            'x-site-ids': 'All',
        });
        return this.http.post(`/reports/${id}`, { reportId: id, isDetail }, { headers });
    }

    public setFilters(filters) {
        this.store.dispatch(set_report_filters({ reportFilters: filters }));
    }

    public selectReport(report) {
        this.store.dispatch(select_report({ report: report }));
    }

    public reloadReport() {
        if (this.currentReportURI) {
            this.loadReport$.next(true);
        }
    }

    public fetchSites() {
        return this.http
            .get('/sites', {
                params: {
                    $select: `name,id,type,business_type,subscriptions,subscriptions_data,menu_status,aliases${this.preferences.preferredLanguage === DEFAULT_LANGUAGE ? '' : `,i18n`}`,
                },
                context: buildIdentifyContext(ENTITY_TYPES.SITE),
            })
            .pipe(
                shareReplay(),
                map((res: ISitesResponse) => {
                    if (res.total && res.items) {
                        return this.prepareSites(res.items);
                    } else {
                        return [];
                    }
                }),
                catchError(() => {
                    this.store.dispatch(add_sites_failure());
                    return of([]);
                }),
            );
    }

    public prepareSites(items: ISite[]) {
        return items.map((item) => ({
            name: item.name,
            id: item.id,
            type: item.type,
            business_type: item.business_type,
            subscriptions: item.subscriptions,
            subscriptions_data: item.subscriptions_data,
            aliases: item.aliases,
            originName: item.originName ?? null,
            menu_status: item.menu_status,
        }));
    }

    public fetchEmployeeBySiteIds(company: string, site: string | Array<string>) {
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = { site };

        return this.http.post('/employees', body, { headers }).pipe(
            map((response: any) => {
                if (response.Data && response.Data.length) {
                    const results = [];
                    response.Data.forEach((item) => {
                        results.push({ itemName: item.name, id: item.employee_id, code: item.employee_corporate_code });
                    });
                    return results;
                } else {
                    return [];
                }
            }),
            catchError((error: any) => throwError(this.errorHandlerService.getErrorMessage(error))),
        );
    }

    public fetchDestinations(request): Observable<Array<{ id: string; name: string }>> {
        const { site, companyId } = request;
        const body = {
            site,
        };
        const headers = new HttpHeaders({
            'x-company-id': companyId,
        });
        return this.http.post<Array<{ id: string; name: string }>>('/filters/destinations', body, { headers }).pipe(
            map((res: any) => {
                if (res.total && res.items) {
                    const results = [];
                    res.items.forEach((item) => {
                        results.push({ id: item.entity_id, name: item.name });
                    });
                    return results;
                } else {
                    return [];
                }
            }),
            catchError(() => of([])),
        );
    }

    public fetchDayParts(request): Observable<Array<string> | any> {
        const { site, companyId } = request;
        const body = {
            site,
        };
        const headers = new HttpHeaders({
            'x-company-id': companyId,
        });
        return this.http.post<Array<string>>('/filters/day-parts', body, { headers }).pipe(
            map((res: any) => {
                if (res.total && res.items) {
                    const results = [];
                    res.items.forEach((item) => {
                        results.push({ id: item.entity_id, name: item.name });
                    });
                    return results;
                } else {
                    return [];
                }
            }),
            catchError(() => of([])),
        );
    }

    public fetchJobCategories(site) {
        site = site[0];
        return this.http
            .post('/job-categories', {
                site,
                date: moment.utc().format(),
            })
            .pipe(
                map((res: any) => {
                    if (res.total && res.items) {
                        const results = [];
                        res.items.forEach((item) => {
                            results.push({ itemName: item.name, id: item.name });
                        });
                        return results;
                    } else {
                        return [];
                    }
                }),
                catchError(() => {
                    this.resetJobCategories();
                    return of([]);
                }),
            );
    }

    public fetchCalendars(site, calendarType) {
        return this.http
            .post('/calendars', {
                site,
                date: moment.utc().format(),
                calendar_type: calendarType,
            })
            .pipe(
                map((res: any) => {
                    if (res && res.length > 0) {
                        return this.prepareCalendars(res);
                    } else {
                        return [];
                    }
                }),
                catchError(() => of([])),
            );
    }

    public prepareCalendars(items) {
        const results = [];
        items.forEach((item) => {
            results.push({
                dateStart: moment(item.dateStart.split('T')[0]).toDate(),
                dateEnd: moment(item.dateEnd.split('T')[0]).toDate(),
            });
        });
        return results;
    }

    public fetchBusinessDate(
        category: string,
        calendarType: CalendarTypeModel,
        siteIds,
        allSites: ISite[],
    ): Observable<any> {
        const data: any = { category, calendarType };
        if (siteIds) {
            data.sites = siteIds;
            data.allSites = allSites.map((site) => site.id);
            this.currentSiteIds = siteIds;
        }
        return this.http.post('/filters/business_date', data).pipe(
            map((res) => res || ''),
            catchError(() => of({ business_date: new Date().toISOString().slice(0, -1).split('T')[0] })),
        );
    }

    public fetchBusinessRuleGroup(site) {
        return this.http
            .post('/filters/business-rule-group', {
                date: moment.utc().format(),
                site: site,
            })
            .pipe(
                map((res: any) => res || []),
                catchError(() => {
                    return of([]);
                }),
            );
    }

    public fetchEtlStatisitcs(reportName) {
        return this.http
            .post('/etl-statistics', {
                name: reportName,
            })
            .pipe(
                catchError(() => []),
                map((res: any) => res || []),
            );
    }

    public resetEtlStatistics() {
        this.store.dispatch(reset_report_etl_statistics());
    }

    public resetBusinessRuleGroup() {
        return this.store.dispatch(set_business_rule_group({ business_rule_groups: [] }));
    }

    public acceptFilters(uri) {
        this.currentReportURI = uri;
        return this.store.dispatch(set_report_url({ reportUrl: uri }));
    }

    public resetJobCategories() {
        return this.store.dispatch(add_job_categories({ job_categories: [] }));
    }

    public resetFilters() {
        return this.store.dispatch(reset_filters());
    }

    public getDmErrorMessage(err, defaultError) {
        let error = {};
        if (err['_body']) {
            error = JSON.parse(err['_body']);
            if (error) {
                if (error['error']) {
                    return error['error'];
                } else {
                    return defaultError;
                }
            } else {
                return defaultError;
            }
        } else {
            return defaultError;
        }
    }

    public prepareSiteIdFromDependencies(selectedSite, sites, singleSiteSelection, type) {
        let sitesToWork = selectedSite;
        if (selectedSite === 'All') {
            sitesToWork = sites;
        }

        if (!sitesToWork) {
            return null;
        }

        if (singleSiteSelection) {
            return sitesToWork ? [sitesToWork.value[0]['id']] : null;
        } else {
            switch (type) {
                case 'string':
                    return sitesToWork.value.map((site) => site.id || site).join(',');
                case 'array':
                    return sitesToWork.value.map((site) => site.id || site);
            }
        }
        return null;
    }

    public fetchEvents(companyId, dataFilter, defaultSort) {
        return this.portalHttp
            .get(
                `/companies/${companyId}/events?$filter=${dataFilter}&$orderby=${defaultSort}&$select=${EVENT_SELECTOR_SELECT}`,
                { context: buildIdentifyContext(ENTITY_TYPES.EVENTS).set(NEEDS_X_LOCALES, true) },
            )
            .pipe(
                shareReplay(),
                map((res: IEventsResponse) =>
                    res.items.map((item) => ({
                        name: item.name,
                        id: item.id,
                        event_day_ordering: item.event_day_ordering,
                        event_days: item.event_days,
                        status: item.status,
                        originName: item.originName ?? null,
                    })),
                ),
                catchError((error) => {
                    const { statusText } = this.errorHandlerService.getErrorMessage(
                        error?.error?.errors?.[0],
                        'events',
                    );
                    this.notificationService.error(statusText || 'Unknown error');
                    return of([]);
                }),
            );
    }

    public fetchEventsOrderDates() {
        return this.http.get(`/event-period/`, {}).pipe(
            shareReplay(),
            map((res: IEventPeriodDataResp) => {
                const ret = {};

                res.data.forEach((item) => {
                    const { order_start_date, order_end_date, event_id } = item;

                    ret[event_id] = ret[event_id]
                        ? {
                              order_start_date: getMinDate([ret[event_id].order_start_date, order_start_date]),
                              order_end_date: getMaxDate([ret[event_id].order_end_date, order_end_date]),
                          }
                        : { order_start_date, order_end_date };
                });

                return {
                    eventsOrderDateRange: ret,
                    originalEventsOrderDateRange: res.data,
                };
            }),
            catchError(() => of({})),
        );
    }

    public fetchCompanyProducts(
        companyId: string,
        selectedSites: ISite[],
    ): Observable<{ products: string[]; total: number }> {
        const headers = new HttpHeaders({
            'x-company-id': companyId,
        });
        const body = {
            site: selectedSites.map((site) => site.id),
        };

        return this.http.post<{ products: string[]; total: number }>(`/company-products`, body, { headers }).pipe(
            map((res) => res),
            catchError(() => of({ products: [], total: 0 })),
        );
    }

    public getDetailViewTitle(type: string = 'DETAIL_VIEW'): string {
        let title = '';
        this.store
            .select(selectActiveReport)
            .pipe(
                take(1),
                map((parentReport) => {
                    return (parentReport as IReport)?.custom_report_title;
                }),
            )
            .subscribe((customReportTitle) => {
                title = customReportTitle + ': ' + this.translations.instant(type);
            });

        return title;
    }

    public xcatQAFlatted(questions: QuestionType[]) {
        const flattedQuestions = [];

        questions.forEach((question, index) => {
            flattedQuestions.push({
                ...question,
                questionIndexDes: `Question${index + 1}: ${question.question}`,
            });

            if (question?.child_questions?.length) {
                const childQuestion = question.child_questions.find((child) => child.related_type === question.answer);

                if (childQuestion) {
                    flattedQuestions.push({
                        ...childQuestion,
                        questionIndexDes: `Question${index + 1}-1: ${childQuestion.question}`,
                    });
                }
            }
        });

        return flattedQuestions;
    }
}
